import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { IToastContext, ToastContext } from "../../../providers/Toast";
import { AuthState } from "../../../context/auth/auth/reducer";
import { Link, useParams } from "react-router-dom";
import { getEventDetail, getRelatedEventsByTags } from "../../../context/events/action";
import moment from "moment";
import { useTranslation } from "react-i18next";
import Button from "../../../components/shared/button/Button";
import { composePathWithParams, getYouTubeIdFromUrl } from "../../../utils/url";
import { ROUTES } from "../../../routing/routes";
import CustomCheckbox from "../../../components/shared/forms/checkbox/CustomCheckbox";
import { useFormik } from "formik";
import * as Yup from "yup";
import EventDetailJitsiContainer from "./jitsi/container";
import EventDetailZoomContainer from "./zoom/container";
import Breadcrumbs from "../../../components/shared/breadcrumbs/Breadcrumbs";
import { Event } from "../../../data/interfaces";
import AddToCalendar from "react-add-to-calendar";
import CountDownTimer from "../../../components/shared/countDown/CountDownTimer";
import _environment from "../../../_environment/_environment";
import EventsList from "../../../components/events/list";
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from "../../../utils/analytics";
import Tooltip from "../../../components/shared/tooltip/Tooltip";

declare const $: any;

// -----------------------------------------------------------------
// I n t e r f a c e s
// -----------------------------------------------------------------
interface Props {
  breadcrumbs: any[];
}

declare const window: any;

const EventDetailView = ({ breadcrumbs, ...restProps }: Props) => {
  // -----------------------------------------------------------------
  // L o c a l   v a r s
  // -----------------------------------------------------------------
  // Access to toast manager hook that you may use to show toasts.
  const { openToast } = useContext<IToastContext>(ToastContext);

  // Access to i18n instance that you may use to translate your content.
  const { t } = useTranslation();

  // Returns an object of key/value pairs of URL parameters that you may use to access match.params of the current <Route>.
  let { id } = useParams();

  // -----------------------------------------------------------------
  // f o r m
  // -----------------------------------------------------------------
  // Formik Initialisation data
  const form = useFormik({
    initialValues: {
      videoRecordingConsent: false,
    },
    validationSchema: Yup.object().shape({
      videoRecordingConsent: Yup.boolean().equals([true]),
    }),
    onSubmit: (values: any) => {
    },
  });

  // -----------------------------------------------------------------
  // N a v i g a t i o n   v a r s
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // S t a t e
  // -----------------------------------------------------------------
  // Event
  const [event, setEvent] = useState<Event | null>(null);
  const [isLoadingEvent, setIsLoadingEvent] = useState<boolean>(false);
  const [isRoomInitialized, setRoomInitialized] = useState<boolean>(false);
  const [isJoinButtonPressed, setJoinButtonPressed] = useState<boolean>(false);

  // Event isStarted
  const [isStarted, setIsStarted] = useState<boolean>(false);

  // Event expired
  const [isExpired, setIsExpired] = useState<boolean>(false);

  // Video playing
  const [isVideoRecordedPlaying, setIsVideoRecordedPlaying] = useState<boolean>(false);

  // Related events
  const [relatedEvents, setRelatedEvents] = useState<Event[]>([]);
  const [isLoadingRelatedEvents, setIsLoadingRelatedEvents] = useState<boolean>(false);

  // Error (event not found)
  const [eventNotFound, setEventNotFound] = useState<boolean>(false);
  // -----------------------------------------------------------------
  // R e f s  (DOM)
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // u s e S e l e c t o r   m e t h o d s  (redux)
  // -----------------------------------------------------------------
  const { user } = useSelector(({ authReducer }: { authReducer: AuthState }) => {
    return authReducer;
  });

  // -----------------------------------------------------------------
  // W o r k i n g   m e t h o d s
  // -----------------------------------------------------------------
  const loadRelatedEvents = async (id: string, tags?: string[]) => {
    if (!tags || !tags.length) {
      return;
    }

    setIsLoadingRelatedEvents(true);

    try {
      // TODO: handle pagination in future
      const data = await getRelatedEventsByTags(id, tags);

      setRelatedEvents(data);
    } catch (err) {
      console.error(err);
      openToast({
        type: "error",
        title: "errors.general.title",
        message: "errors.general.description",
        callback: () => {
        },
      });
    } finally {
      setIsLoadingRelatedEvents(false);
    }
  };

  /**
   * Load event from firestore
   * @function
   * @async
   *
   * @param id
   */
  const loadEvent = async (id: string) => {
    setIsLoadingEvent(true);

    console.log("load event");

    try {
      const data = await getEventDetail(id);

      setEvent(data);

      // Track event
      trackAnalyticsEvent(ANALYTICS_EVENTS.EVENT_VISIT, {
        event: {
          id: data.id,
          name: data.name,
        },
      });

      loadRelatedEvents(id, data.tags);
    } catch (err) {
      console.error(err);

      setEventNotFound(true);

      // openToast({
      //   type: "error",
      //   title: "errors.general.title",
      //   message: "errors.general.description",
      //   callback: () => {},
      // });
    } finally {
      setIsLoadingEvent(false);
    }
  };

  /**
   * Scrolls to description area
   * @function
   */
  const scrollToDesc = () => {
    $("html,body").animate({ scrollTop: $("#event-description").offset().top - 110 }, 800);
  };

  // -----------------------------------------------------------------
  // R e n d e r   m e t h o d s
  // -----------------------------------------------------------------
  const renderRelatedEvents = () => {
    return (
      <>
        <br/>
        <h2 className="mb-4">{t("pages.eventsList.related.title")}</h2>

        {!!isLoadingRelatedEvents && (
          <div className="list-loading">
            <i className="fas fa-spin fa-spinner"/>
          </div>
        )}

        {!isLoadingRelatedEvents && (
          <>
            {!!relatedEvents && relatedEvents.length > 0 && (
              <div className={`events-list ${relatedEvents.length === 0 ? "no-data" : ""}`}>
                <EventsList events={relatedEvents} groupByDay={false}/>
              </div>
            )}

            {(!relatedEvents || !relatedEvents.length) && (
              <p>{t("pages.eventsList.related.noData")}</p>
            )}
          </>
        )}
      </>
    );
  };

  // -----------------------------------------------------------------
  // L i f e c y c l e
  // -----------------------------------------------------------------
  /**
   * This method is called the first time the component is mounted
   * @function
   */
  const init = (): void => {
    // init component
    console.log("[EventDetailView] init");
  };

  /**
   * This method is called when the component is unmounted
   * @function
   */
  const destroy = (): void => {
    // destroy component
    console.log("[EventDetailView] destroy");
  };

  // -----------------------------------------------------------------
  // u s e E f f e c t   m e t h o d s
  // -----------------------------------------------------------------
  /**
   * This hook is called once when the component is mounted
   */
  useEffect(() => {
    init();
    return () => {
      destroy();
    };
  }, []);

  /**
   * Hook to event in order to handle expiration state
   */
  useEffect(() => {
    if (event) {
      setIsExpired(moment.unix(event.end_datetime.seconds).isBefore(moment()));
      setIsStarted(moment.unix(event.start_datetime.seconds).isSameOrBefore(moment()));
    }
  }, [event]);

  /**
   * Hook to isExpired in order play recorded video if event is expired
   */
  useEffect(() => {
    if (isExpired && event && event.recording_url) {
      setIsVideoRecordedPlaying(true);
    }
  }, [isExpired]);

  /**
   * Hook to id in order to load event
   */
  useEffect(() => {
    if (id) {
      // Load event detail
      loadEvent(id);
    }
  }, [id]);

  /**
   * Hook to isExpired state in order to autoplay youtube
   */
  // useEffect(() => {
  //   if (isExpired) {
  //     if (event && event.recording_url) {
  //       setIsVideoRecordedPlaying(true);
  //     }
  //   }
  // }, [isExpired]);

  // -----------------------------------------------------------------
  // T e m p l a t e
  // -----------------------------------------------------------------
  return (
    <div>
      {/* Loading */}
      {!!isLoadingEvent && (
        <div className="list-loading">
          <i className="fas fa-spin fa-spinner"/>
        </div>
      )}

      {/* Event not found */}
      {!!eventNotFound && (
        <div className="text-center">
          <h1>{t("pages.notFound.events.introTitle")}</h1>
          <p>{t("pages.notFound.events.description")}</p>
          <img src="/assets/images/undraw/not_found.svg" className="img-responsive mw-600 mt-5"/>
          <Link className="btn btn-link mt-5" to={ROUTES.EVENTS}>
            {t("pages.notFound.events.backToEvents")}
          </Link>
        </div>
      )}

      {/* Event page */}
      {!isLoadingEvent && event && (
        <div id={"event-detail"} className="page-content">
          <div className={"container"}>
            <Breadcrumbs labelReplacers={{ ":eventName": event.name, ":standName": event.stand?.name }}/>
            <div className={"row"}>
              {/* Left column */}
              <div className={"col-xs-12 col-sm-8 col-md-8 col-lg-9"}>
                <header className={"clearfix"}>
                  <div className={"info"}>
                    <h1 className="page-title">{event.name}</h1>
                    {event.tags?.map((tag, i) => (
                      <Link key={tag} to={`${ROUTES.SEARCH}?query=${tag}`} className="tag">
                        #{tag}
                      </Link>
                    ))}
                  </div>
                </header>

                <div id={"room-iframe-wrapper"}>
                  {/* If event has not started, show countdown */}
                  {!isStarted && (
                    <div className="countdown-wrapper">
                      <CountDownTimer
                        date={moment.unix(event.start_datetime.seconds).toDate()}
                        onStart={() => {
                          setIsStarted(true);
                        }}
                      />
                    </div>
                  )}

                  {/* If event has started (or is starting) */}
                  {!!isStarted && (
                    <>
                      {isJoinButtonPressed &&
                      event.jitsi?.meeting_name &&
                      event.jitsi.meeting_name && (
                        <EventDetailJitsiContainer
                          event={event}
                          roomInitializedCallback={(res: boolean) => {
                            setRoomInitialized(res);
                          }}
                        />
                      )}
                      {isJoinButtonPressed && event.zoom?.meeting_id && event.id && (
                        <EventDetailZoomContainer
                          event={event}
                          roomInitializedCallback={(res: boolean) => {
                            setRoomInitialized(res);
                          }}
                        />
                      )}
                      {isExpired ? (
                        <>
                          {!isVideoRecordedPlaying ? (
                            <div className={"message"}>
                              <h3>{t("pages.eventDetail.expiredMessage.title")}</h3>
                              {event.recording_url && (
                                <span>{t("pages.eventDetail.expiredMessage.description")}</span>
                              )}
                            </div>
                          ) : (
                            <>
                              {event.recording_url && (
                                <iframe
                                  width="560"
                                  height="315"
                                  className={"recorded-video"}
                                  src={`https://www.youtube.com/embed/${getYouTubeIdFromUrl(
                                    event.recording_url,
                                  )}?autoplay=1&modestbranding=1`}
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                />
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          {isJoinButtonPressed ? (
                            <i className={"fas fa-spinner fa-spin loading"}/>
                          ) : (
                            <>
                              {!isRoomInitialized && (
                                <div className={"message"}>
                                  <h3>{t("pages.eventDetail.joinMessage.title")}</h3>
                                  <span>{t("pages.eventDetail.joinMessage.description")}</span>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Right column */}
              <div className={"col-xs-12 col-sm-4 col-md-4 col-lg-3"}>
                <div className={"info"}>
                  <div className={"content"}>
                    <h2>{t("pages.eventDetail.info")}</h2>

                    {!!event.congress_room && (
                      <div className="value">
                        <i className="fas fa-thumbtack"></i>
                        {event.congress_room}
                      </div>
                    )}

                    <div className="value">
                      <i className="far fa-calendar"></i>
                      {moment.unix(event.start_datetime.seconds).format("dddd, MMMM Do YYYY")}
                    </div>

                    <div className="value">
                      <i className="far fa-clock"></i>
                      {moment.unix(event.start_datetime.seconds).format("HH:mm")}
                      &nbsp;-&nbsp;
                      {moment.unix(event.end_datetime.seconds).format("HH:mm")}
                    </div>

                    {!!event.relators && !!event.relators.length && (
                      <div className={"value"}>
                        <i className={"fas fa-users"}/>
                        {event.relators.map((r: string) => `${r}`).join(", ")}
                      </div>
                    )}

                    {!isStarted && !isExpired && (
                      <div className="add-to-calendar">
                        <AddToCalendar
                          event={{
                            title: event.name,
                            description: event.description,
                            startTime: moment.unix(event.start_datetime.seconds).toISOString(),
                            endTime: moment.unix(event.end_datetime.seconds).toISOString(),
                            location: composePathWithParams(
                              ROUTES.EVENT_DETAIL,
                              [{ id: event?.id }],
                              true,
                            ),
                          }}
                          buttonTemplate={{ " far fa-calendar-plus": "left" }}
                          displayItemIcons={false}
                          buttonLabel={t("components.eventBox.addToCalendar")}
                        />
                      </div>
                    )}

                    {/* Show live indicator */}
                    {isStarted && !isExpired && (
                      <div className="live-indicator">
                        <span></span>
                        <small>Live now</small>
                      </div>
                    )}

                    {/* Button to read desc */}
                    <div className={"value primary read-more"} onClick={scrollToDesc}>
                      <i className={"fas fa-info-circle"}/>
                      {t("pages.eventDetail.see_description")}
                    </div>
                  </div>
                  <footer>
                    {/* Join / Watch buttons */}
                    {!isRoomInitialized && !isExpired && !!isStarted && (
                      <>
                        <div className="form-group">
                          <CustomCheckbox
                            label={t("gdpr.videoRecording")}
                            name="videoRecordingConsent"
                            id="videoRecordingConsent"
                            form={form}
                            disabled={isJoinButtonPressed}
                            onBlur={form.handleBlur}
                            value={form.values.videoRecordingConsent}
                            errors={form.errors.videoRecordingConsent}
                            touched={form.touched.videoRecordingConsent}
                          />
                        </div>

                        <div
                          data-type="error"
                          data-tip-disable={!!form.values.videoRecordingConsent}
                          data-tip={t("tooltips.events.detail.acceptPolicy")}
                        >
                          <Button
                            disabled={
                              // !isStarted ||
                              isJoinButtonPressed ||
                              !form.isValid ||
                              !form.values.videoRecordingConsent
                            }
                            onClick={() => setJoinButtonPressed(true)}
                            className={"btn btn-primary btn-user btn-block"}
                          >
                            {!!isStarted && t("pages.eventDetail.join")}
                            {/* {!isStarted && t("pages.eventDetail.not_started")} */}
                          </Button>
                          <Tooltip/>
                        </div>
                      </>
                    )}
                    {isExpired && event.recording_url && (
                      <a
                        className={"btn btn-primary btn-user btn-block icon-left"}
                        onClick={() => {
                          setIsVideoRecordedPlaying(!isVideoRecordedPlaying);
                        }}
                      >
                        <i className={`fas fa-${isVideoRecordedPlaying ? "stop" : "play"}`}/>
                        {t(`pages.eventDetail.${isVideoRecordedPlaying ? "stop" : "seeAgain"}`)}
                      </a>
                    )}

                    {/* Sponsored by */}
                    <hr/>
                    <div className="sponsored-by">
                      <p>{t("pages.eventDetail.poweredBy")}</p>
                      <img
                        src={
                          event.image_url ? event.image_url : _environment.assets.eventPlaceholder
                        }
                        alt={event.name}
                      />
                    </div>

                    {/* {event.stand && (
                        <div className={"stand"}>
                          <hr />
                          <a
                            onClick={() => {
                              history.push(
                                composePathWithParams(ROUTES.STAND_DETAIL, [
                                  { id: event?.stand?.id },
                                ])
                              );
                            }}
                          >
                            <img
                              className={"img-responsive"}
                              src={event.stand.logo_image_url}
                              alt={event.stand.name}
                            />
                          </a>
                        </div>
                      )} */}
                  </footer>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="container mt-5" id="event-description">
            <div className="box">
              <h1 className="page-title">Description</h1>
              <div
                className={"description"}
                dangerouslySetInnerHTML={{
                  __html: event.description,
                }}
              />
            </div>
          </div>

          {/* Related events */}
          <div className="container mt-5">{renderRelatedEvents()}</div>
        </div>
      )}
    </div>
  );
};
export default EventDetailView;
